import { useNarrativeStore, useWorldStore } from '@/engine/state';
import type { PendingConsequence } from '@/types';

/**
 * Engine de consecuencias diferidas.
 *
 * Modelo:
 * - Una decisión puede programar una consequence: se crea una entrada
 *   PendingConsequence en worldStore y la entrada del log apunta al id.
 * - Más tarde, alguna escena (u otro trigger) resuelve la consequence
 *   mostrando su impacto real. Se actualiza la entrada del log y se
 *   quita el PendingConsequence.
 */

export interface ProgramarConsecuenciaInput {
  consequenceId: string;
  triggeredBy: string;
  resumenOpaco: string;
  decisionEntryId: string;
}

/** Crea una consecuencia pendiente vinculada a una decisión. */
export function programarConsecuencia(input: ProgramarConsecuenciaInput): void {
  const world = useWorldStore.getState();
  // Si ya existía, le sumamos el decisionEntryId al bucket.
  const existente = world.consecuenciasPendientes.find(
    (c) => c.id === input.consequenceId,
  );
  if (existente) {
    useWorldStore.setState((s) => ({
      consecuenciasPendientes: s.consecuenciasPendientes.map((c) =>
        c.id === input.consequenceId
          ? { ...c, decisionEntryIds: [...c.decisionEntryIds, input.decisionEntryId] }
          : c,
      ),
    }));
    useWorldStore.getState().persistir();
    return;
  }
  const nueva: PendingConsequence = {
    id: input.consequenceId,
    triggeredBy: input.triggeredBy,
    resumenOpaco: input.resumenOpaco,
    decisionEntryIds: [input.decisionEntryId],
  };
  world.agregarConsecuenciaPendiente(nueva);
}

/**
 * Resuelve una consecuencia: actualiza las entradas de log asociadas
 * con el impacto final y la quita del set de pendientes.
 * Devuelve true si efectivamente había una pendiente con ese id.
 */
export function resolverConsecuencia(
  consequenceId: string,
  impactoFinal: string,
): boolean {
  const world = useWorldStore.getState();
  const pendiente = world.consecuenciasPendientes.find((c) => c.id === consequenceId);
  if (!pendiente) return false;

  const narrative = useNarrativeStore.getState();
  narrative.resolverConsecuencia(consequenceId, impactoFinal);
  world.resolverConsecuenciaPendiente(consequenceId);
  return true;
}

/**
 * Devuelve las consecuencias pendientes que deben resolverse
 * cuando se entra a una escena con este trigger id.
 */
export function consecuenciasParaTrigger(triggerId: string): PendingConsequence[] {
  return useWorldStore
    .getState()
    .consecuenciasPendientes.filter((c) => c.triggeredBy === triggerId);
}
